import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { FeedSourceListItem } from '@news-cms/shared';
import { ApiError, api } from './api';

export interface MgrInboxItem {
  id: string;
  topic: string;
  intro: string;
  publishedAt: string | null;
  imageUrl: string;
  thumbUrl: string;
  /** Upstream category label (e.g. "การเมือง"). NULL for rows polled
   * before migration 0029 or when upstream omitted it. */
  categoryName: string | null;
  status: 'new' | 'imported';
  importedPostId: string | null;
}

export interface MgrImportResult {
  postId: string;
  postSlug: string;
  mediaId: string;
  alreadyImported: boolean;
}

export interface MgrRefreshResult {
  ok: true;
  inserted: number;
  cleaned: number;
}

/** Canonical default source id — mirrors backend's MGR_SOURCE_ID. The FE
 *  uses this when no `?source=` is present in the URL so existing
 *  bookmarks keep landing on the original Manager Online inbox. */
export const DEFAULT_MGR_SOURCE_ID = 'src-mgronline';

/** The tab shown on landing (URL has no `?source=`). Distinct from
 *  DEFAULT_MGR_SOURCE_ID (which stays the protected/back-compat sentinel used
 *  for delete-guards + lib fallbacks) — this only controls which tab is active
 *  + gets the clean URL. */
export const LANDING_SOURCE_ID = 'src-mgronline-firstsection';

/** Append `?sourceId=` to an admin endpoint URL when callers target a
 *  non-default source. Keeping the URL untouched for the default avoids
 *  cache-busting on the most common path. */
function withSource(path: string, sourceId: string): string {
  if (sourceId === DEFAULT_MGR_SOURCE_ID) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}sourceId=${encodeURIComponent(sourceId)}`;
}

export function mgrInboxQueryOptions(sourceId: string = DEFAULT_MGR_SOURCE_ID) {
  return queryOptions({
    queryKey: ['feed', 'mgronline', sourceId, 'list'] as const,
    queryFn: () =>
      api<{ items: MgrInboxItem[] }>(withSource('/api/admin/feed/mgronline', sourceId)).then(
        (d) => d.items,
      ),
    // Worker caches upstream in KV for 10 min — match that on the client so
    // we don't show stale "new" status after re-import in another tab.
    staleTime: 60_000,
  });
}

/** GET /source returns the source row + the separately-stored c.php URL
 * (which lives in the settings table, not feed_sources). The dialog needs
 * both, so we keep them grouped in a wrapper rather than splitting into
 * two queries. */
export interface MgrSourceInfo {
  source: FeedSourceListItem;
  detailUrl: string;
}

/** Current source row + detail URL — drives the settings dialog prefill
 * and the "last polled" hint. */
export function mgrSourceQueryOptions(sourceId: string = DEFAULT_MGR_SOURCE_ID) {
  return queryOptions({
    queryKey: ['feed', 'mgronline', sourceId, 'source'] as const,
    queryFn: () => api<MgrSourceInfo>(withSource('/api/admin/feed/mgronline/source', sourceId)),
    staleTime: 60_000,
  });
}

/** GET /sources — list every mgronline-style row for the FE tab strip.
 *  Includes pendingItemCount per row so the tab badge renders without an
 *  extra query. Defaults to the canonical row when no rows are seeded
 *  (handled by backend ensureMgrSource). */
export const mgrSourcesListQueryOptions = queryOptions({
  queryKey: ['feed', 'mgronline', 'sources'] as const,
  queryFn: () =>
    api<{ items: FeedSourceListItem[] }>('/api/admin/feed/mgronline/sources').then((d) => d.items),
  staleTime: 30_000,
});

export interface CreateMgrSourceInput {
  /** Tab label. */
  name: string;
  /** q.php feed URL (usually carries the category param). */
  url: string;
  fetchIntervalMinutes?: number;
}

/** Create a new mgronline source (inbox tab). Returns the created row. */
export function useCreateMgrSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMgrSourceInput) =>
      api<{ source: FeedSourceListItem }>('/api/admin/feed/mgronline/sources', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((d) => d.source),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mgrSourcesListQueryOptions.queryKey });
    },
  });
}

/** Delete a source (tab). The default `src-mgronline` row is rejected by
 *  the backend. Child feed_items cascade. */
export function useDeleteMgrSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/api/admin/feed/mgronline/sources/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mgrSourcesListQueryOptions.queryKey });
    },
  });
}

export function useRefreshMgrFeed(sourceId: string = DEFAULT_MGR_SOURCE_ID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<MgrRefreshResult>(withSource('/api/admin/feed/mgronline/refresh', sourceId), {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feed', 'mgronline', sourceId, 'list'] });
      void qc.invalidateQueries({ queryKey: ['feed', 'mgronline', sourceId, 'source'] });
      void qc.invalidateQueries({ queryKey: mgrSourcesListQueryOptions.queryKey });
    },
  });
}

export interface UpdateMgrSourceInput {
  url?: string;
  name?: string;
  isActive?: boolean;
  fetchIntervalMinutes?: number;
  description?: string | null;
  /** c.php endpoint — stored in `settings` table, not feed_sources. */
  detailUrl?: string;
}

export function useUpdateMgrSource(sourceId: string = DEFAULT_MGR_SOURCE_ID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMgrSourceInput) =>
      api<MgrSourceInfo>(withSource('/api/admin/feed/mgronline/source', sourceId), {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feed', 'mgronline', sourceId, 'list'] });
      void qc.invalidateQueries({ queryKey: ['feed', 'mgronline', sourceId, 'source'] });
      void qc.invalidateQueries({ queryKey: mgrSourcesListQueryOptions.queryKey });
    },
  });
}

export function useImportMgrItem(sourceId: string = DEFAULT_MGR_SOURCE_ID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ result: MgrImportResult }>(
        withSource(`/api/admin/feed/mgronline/${id}/import`, sourceId),
        { method: 'POST' },
      ).then((d) => d.result),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feed', 'mgronline', sourceId, 'list'] });
      void qc.invalidateQueries({ queryKey: mgrSourcesListQueryOptions.queryKey });
    },
  });
}

/** Result of a bulk `_bulk-import` call — mirrors the backend `summary`. */
export interface MgrBulkImportSummary {
  requested: number;
  availableTotal: number;
  availableNew: number;
  processed: number;
  imported: number;
  alreadyImported: number;
  failed: Array<{ id: string; reason: string }>;
  categoriesCreated: number;
  tagsAssigned: number;
  tagsCreated: number;
  postIds: string[];
}

export interface BulkImportInput {
  /** Feed-item ids to import (checkbox selection). Always imported as drafts. */
  ids: string[];
}

/** Import a hand-picked set of feed items in one request. The backend runs
 *  them sequentially (idempotent per id) and returns a summary — see
 *  `MgrBulkImportSummary`. */
export function useBulkImportMgrItems(sourceId: string = DEFAULT_MGR_SOURCE_ID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkImportInput) =>
      api<{ summary: MgrBulkImportSummary }>(
        withSource('/api/admin/feed/mgronline/_bulk-import', sourceId),
        { method: 'POST', body: JSON.stringify(input) },
      ).then((d) => d.summary),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feed', 'mgronline', sourceId, 'list'] });
      void qc.invalidateQueries({ queryKey: mgrSourcesListQueryOptions.queryKey });
    },
  });
}

export function mgrErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  switch (e.code) {
    case 'UPSTREAM_ERROR':
      return 'ดึงข้อมูลจาก Manager Online ไม่สำเร็จ — ลองอีกครั้งหรือกด "ดึงใหม่"';
    case 'ITEM_NOT_FOUND':
      return 'รายการนี้ไม่อยู่ใน feed แล้ว — refresh แล้วลองใหม่';
    case 'VALIDATION_ERROR':
      return 'ข้อมูลไม่ถูกต้อง — ตรวจ URL / ค่าอื่นแล้วลองใหม่';
    case 'FORBIDDEN':
      return 'ไม่มีสิทธิ์ — ต้องมี feed.manage';
    case 'NOT_FOUND':
      return 'ไม่พบแหล่งข้อมูล mgronline — รีเฟรชหน้าแล้วลองใหม่';
    default:
      return e.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  }
}
